import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { CardPrintTemplate, DistrictMode } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { archiveMaster, upsertMaster, useCardPrintTemplates } from './useMasters';
import { MasterHistoryView } from './MasterHistoryView';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import { stripSystemFields } from './jsonImportExport';
import { ColorSwatch } from './components/ColorSwatch';
import { ModeSelectField } from './components/ModeSelectField';

/**
 * M7 cardPrintTemplates 管理画面 (docs/04 §3-1 / §3-3 M7、Phase 3.5 admin 第 8 弾 / 最終)。
 *
 * 旧 `DEFAULT_FIELDS_BY_MODE` ハードコードを動的化 (docs/04 §3-3 完全準拠、docs/04 L117 「新規」)。
 *
 * 各 field は:
 *   - key (UI 内識別子)
 *   - label (印刷時の表示ラベル)
 *   - source: 'post' | 'extraFields' | 'aiAnalysis' | 'author' (データソース種別)
 *   - fieldPath (source 内のフィールドパス、e.g. 'aiAnalysis.bousai.classifications[0].category_name')
 *   - style?: { fontSize?, bold?, color? } (任意、未設定は payload から除外)
 *   - displayOrder (数値、保存時に昇順ソート)
 *
 * layout は 'card-v1' | 'card-v2' | 'list-v1' enum (3 種)。
 *
 * Phase 5 で再検討 (R-29):
 *   - 印刷プレビュー UI (現状は schema 編集のみ、layout 視覚化なし)
 *   - source / fieldPath の autocomplete (Post / ExtraFields schema 連動)
 *   - style sub-form の visual editor (font picker / color picker pair)
 */

const COLLECTION_NAME = 'cardPrintTemplates';

type Field = CardPrintTemplate['fields'][number];
type FieldSource = Field['source'];
type Layout = CardPrintTemplate['layout'];

const FIELD_SOURCES: readonly FieldSource[] = ['post', 'extraFields', 'aiAnalysis', 'author'];
const LAYOUTS: readonly Layout[] = ['card-v1', 'card-v2', 'list-v1'];

type FitMode = NonNullable<NonNullable<Field['style']>['fit']>;

interface FieldDraft {
  key: string;
  label: string;
  source: FieldSource;
  fieldPath: string;
  displayOrder: number;
  // style? は draft 段階では平坦化、Save 時に nested object に再構成 (空なら省略)
  styleFontSize: string; // text input、空文字 = 未設定
  styleBold: boolean;
  styleColor: string; // 空文字 = 未設定
  // AW6 follow-up #2: 項目別書式の永続化分。
  styleShowLabel: boolean; // 項目名 (日本語) を「項目名: 値」で表示
  styleResolveCodes: boolean; // AI 分類コード → 日本語名 (M2 マスタ駆動)
  styleMaxChars: string; // 空文字 = 無制限
  styleWrap: 'wrap' | 'nowrap'; // nowrap = 1 行固定
  styleMaxLines: string; // 空文字 = 既定 2
  styleFit: FitMode; // 'fixed' | 'shrink' | 'grow'
}

interface FormDraft {
  id: string;
  displayName: string;
  mode: DistrictMode;
  layout: Layout;
  fields: FieldDraft[];
}

const EMPTY_DRAFT: FormDraft = {
  id: '',
  displayName: '',
  mode: 'ecw',
  layout: 'card-v1',
  fields: [],
};

const EMPTY_FIELD: FieldDraft = {
  key: '',
  label: '',
  source: 'post',
  fieldPath: '',
  displayOrder: 0,
  styleFontSize: '',
  styleBold: false,
  styleColor: '',
  styleShowLabel: false,
  styleResolveCodes: false,
  styleMaxChars: '',
  styleWrap: 'wrap',
  styleMaxLines: '',
  styleFit: 'fixed',
};

const INPUT_CLASS =
  'w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400';

export function fieldToDraft(f: Field): FieldDraft {
  return {
    key: f.key,
    label: f.label,
    source: f.source,
    fieldPath: f.fieldPath,
    displayOrder: f.displayOrder,
    styleFontSize: f.style?.fontSize !== undefined ? String(f.style.fontSize) : '',
    styleBold: f.style?.bold === true,
    styleColor: f.style?.color ?? '',
    styleShowLabel: f.style?.showLabel === true,
    styleResolveCodes: f.style?.resolveCodes === true,
    styleMaxChars: f.style?.maxChars !== undefined ? String(f.style.maxChars) : '',
    styleWrap: f.style?.wrap === false ? 'nowrap' : 'wrap',
    styleMaxLines: f.style?.maxLines !== undefined ? String(f.style.maxLines) : '',
    styleFit: f.style?.fit ?? 'fixed',
  };
}

export function draftToField(d: FieldDraft): Field {
  const result: Field = {
    key: d.key.trim(),
    label: d.label.trim(),
    source: d.source,
    fieldPath: d.fieldPath.trim(),
    displayOrder: d.displayOrder,
  };
  const style: NonNullable<Field['style']> = {};
  if (d.styleFontSize.trim()) {
    const n = Number(d.styleFontSize);
    // 正の有限値のみ採用し 4..48pt にクランプ (0/負値で描画が壊れるのを防ぐ)。
    if (Number.isFinite(n) && n > 0) style.fontSize = Math.min(48, Math.max(4, n));
  }
  if (d.styleBold) style.bold = true;
  if (d.styleColor.trim()) style.color = d.styleColor.trim();
  if (d.styleShowLabel) style.showLabel = true;
  if (d.styleResolveCodes) style.resolveCodes = true;
  if (d.styleMaxChars.trim()) {
    const n = Number(d.styleMaxChars);
    if (Number.isFinite(n) && n > 0) style.maxChars = Math.floor(n);
  }
  if (d.styleWrap === 'nowrap') style.wrap = false;
  // maxLines は折り返しあり (wrap) のときのみ保存 (nowrap と矛盾する死にデータを残さない)。
  if (d.styleWrap !== 'nowrap' && d.styleMaxLines.trim()) {
    const n = Number(d.styleMaxLines);
    if (Number.isFinite(n) && n > 0) style.maxLines = Math.min(8, Math.max(1, Math.floor(n)));
  }
  if (d.styleFit !== 'fixed') style.fit = d.styleFit;
  // style サブフィールドが 1 つも設定されていなければ style キーごと省略
  if (Object.keys(style).length > 0) result.style = style;
  return result;
}

/**
 * インポート JSON の field.style を sanitize (エディタの draftToField と同等の防御)。
 * 不正値 (負/0/小数 fontSize・maxChars・maxLines、未知の fit) を drop / クランプし、
 * 無検証 JSON が helper 群に不正値を流し込むのを防ぐ。
 */
export function sanitizeFieldStyle(raw: unknown): Field['style'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as Record<string, unknown>;
  const out: NonNullable<Field['style']> = {};
  if (typeof s.fontSize === 'number' && Number.isFinite(s.fontSize) && s.fontSize > 0) {
    out.fontSize = Math.min(48, Math.max(4, s.fontSize));
  }
  if (s.bold === true) out.bold = true;
  if (typeof s.color === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(s.color.trim())) {
    out.color = s.color.trim();
  }
  if (s.showLabel === true) out.showLabel = true;
  if (s.resolveCodes === true) out.resolveCodes = true;
  if (typeof s.maxChars === 'number' && Number.isFinite(s.maxChars) && s.maxChars > 0) {
    out.maxChars = Math.floor(s.maxChars);
  }
  if (s.wrap === false) out.wrap = false;
  if (
    out.wrap !== false &&
    typeof s.maxLines === 'number' &&
    Number.isFinite(s.maxLines) &&
    s.maxLines > 0
  ) {
    out.maxLines = Math.min(8, Math.max(1, Math.floor(s.maxLines)));
  }
  if (s.fit === 'shrink' || s.fit === 'grow') out.fit = s.fit;
  return Object.keys(out).length > 0 ? out : undefined;
}

function validateCardPrintTemplate(raw: unknown): (CardPrintTemplate & { id: string }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id.trim()) return null;
  if (typeof r.displayName !== 'string') return null;
  if (typeof r.mode !== 'string') return null;
  if (typeof r.layout !== 'string') return null;
  if (!Array.isArray(r.fields)) return null;
  // 各 field の style を sanitize (無検証 JSON の不正 style を弾く)。
  r.fields = (r.fields as unknown[]).map((f) => {
    if (!f || typeof f !== 'object') return f;
    const fr = f as Record<string, unknown>;
    const cleaned = sanitizeFieldStyle(fr.style);
    if (cleaned) fr.style = cleaned;
    else delete fr.style;
    return fr;
  });
  return r as unknown as CardPrintTemplate & { id: string };
}

export function CardPrintTemplatesPage() {
  const state = useCardPrintTemplates();
  const { toast } = useToast();
  const [editing, setEditing] = useState<FormDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; name: string } | null>(null);

  async function handleImport(importedItems: (CardPrintTemplate & { id: string })[]) {
    for (const it of importedItems) {
      const existing =
        state.status === 'ready' ? state.items.find((x) => x.id === it.id) : undefined;
      // Sprint BG-MasterIO follow-up — system field を除去
      const { id, ...rest } = stripSystemFields(it);
      await upsertMaster<CardPrintTemplate>({
        collectionName: COLLECTION_NAME,
        id: id as string,
        uid: 'admin-stub',
        isNew: !existing,
        data: rest as Omit<
          CardPrintTemplate,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
    }
  }

  function handleNew() {
    setEditing({ ...EMPTY_DRAFT, fields: [] });
    setIsNew(true);
  }

  function handleEdit(item: CardPrintTemplate & { id: string }) {
    setEditing({
      id: item.id,
      displayName: item.displayName,
      mode: item.mode,
      layout: item.layout,
      fields: item.fields.map(fieldToDraft),
    });
    setIsNew(false);
  }

  function addField() {
    if (!editing) return;
    const nextOrder =
      editing.fields.length === 0 ? 0 : Math.max(...editing.fields.map((f) => f.displayOrder)) + 1;
    setEditing({
      ...editing,
      fields: [...editing.fields, { ...EMPTY_FIELD, displayOrder: nextOrder }],
    });
  }

  function updateField(index: number, patch: Partial<FieldDraft>) {
    if (!editing) return;
    const target = editing.fields[index];
    if (!target) return;
    const next = [...editing.fields];
    next[index] = { ...target, ...patch };
    setEditing({ ...editing, fields: next });
  }

  function removeField(index: number) {
    if (!editing) return;
    setEditing({ ...editing, fields: editing.fields.filter((_, i) => i !== index) });
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.displayName.trim()) {
      toast('error', 'displayName は必須です');
      return;
    }
    if (isNew && !editing.id.trim()) {
      toast('error', '新規時は id (kebab-case) を指定してください');
      return;
    }
    // fields[] validation
    const cleaned: Field[] = [];
    const seenKeys = new Set<string>();
    let fIndex = 0;
    for (const d of editing.fields) {
      fIndex++;
      const key = d.key.trim();
      const label = d.label.trim();
      const fieldPath = d.fieldPath.trim();
      if (!key || !label || !fieldPath) {
        toast('error', `field ${fIndex}: key / label / fieldPath は必須です`);
        return;
      }
      if (seenKeys.has(key)) {
        toast('error', `field ${fIndex}: key "${key}" が重複しています`);
        return;
      }
      seenKeys.add(key);
      if (!Number.isFinite(d.displayOrder)) {
        toast('error', `field ${fIndex}: displayOrder の数値が不正です`);
        return;
      }
      if (d.styleFontSize.trim() && !Number.isFinite(Number(d.styleFontSize))) {
        toast('error', `field ${fIndex}: style.fontSize の数値が不正です`);
        return;
      }
      cleaned.push(draftToField(d));
    }
    cleaned.sort((a, b) => a.displayOrder - b.displayOrder);
    setSaving(true);
    try {
      await upsertMaster<CardPrintTemplate>({
        collectionName: COLLECTION_NAME,
        id: editing.id || undefined,
        uid: 'admin-stub', // Phase 5 で実 master role uid に置換
        isNew,
        data: {
          mode: editing.mode,
          displayName: editing.displayName.trim(),
          layout: editing.layout,
          fields: cleaned,
          enabled: true,
          version: 1,
          schemaVersion: 1,
          archivedAt: null,
        },
      });
      toast('success', isNew ? '新規作成しました' : '更新しました');
      setEditing(null);
      setIsNew(false);
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(id: string, name: string) {
    if (!confirm(`「${name}」をアーカイブしますか? (物理削除ではなく archivedAt 設定)`)) return;
    try {
      await archiveMaster(COLLECTION_NAME, id, 'admin-stub'); // Phase 5 で実 master role uid に置換
      toast('success', 'アーカイブしました');
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">カード印刷テンプレマスター</h1>
        <p className="text-sm text-slate-500 mt-1">
          カード印刷レイアウト (内部 ID: cardPrintTemplates、docs/04 §3-1 / §3-3 M7)。旧
          DEFAULT_FIELDS_BY_MODE ハードコードを動的化。
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Button variant="primary" size="md" onClick={handleNew} disabled={editing !== null}>
          ＋ 新規作成
        </Button>
        <MasterImportExportButtons<CardPrintTemplate & { id: string }>
          collectionName={COLLECTION_NAME}
          items={state.status === 'ready' ? state.items : []}
          validator={validateCardPrintTemplate}
          onImport={handleImport}
          disabled={editing !== null}
          expectedMode={['ecw', 'bousai', 'walker', 'kids-bousai']}
        />
      </div>

      {state.status === 'loading' && <p className="text-slate-500">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-danger-600">読込エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            件数: {state.items.length} (アーカイブ済除外)
          </p>
          <ul className="grid grid-cols-1 gap-2 mb-6">
            {state.items.map((item) => (
              <li key={item.id}>
                <Card padding="md" className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <strong className="text-slate-900">{item.displayName}</strong>
                    <span className="ml-2 text-xs text-slate-500">id: {item.id}</span>
                    <span className="ml-2 text-xs text-slate-500">mode: {item.mode}</span>
                    <span className="ml-2 text-xs text-slate-500">layout: {item.layout}</span>
                    <span className="ml-2 text-xs text-slate-500">
                      fields: {item.fields.length}
                    </span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <RowExportButton collectionName={COLLECTION_NAME} item={item} />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEdit(item)}
                      aria-label={`${item.displayName} を編集`}
                    >
                      編集
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setHistoryTarget({ id: item.id, name: item.displayName })}
                      aria-label={`${item.displayName} の履歴を表示`}
                    >
                      履歴
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => void handleArchive(item.id, item.displayName)}
                      aria-label={`${item.displayName} をアーカイブ`}
                    >
                      アーカイブ
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}

      {historyTarget && (
        <MasterHistoryView
          collectionName={COLLECTION_NAME}
          masterId={historyTarget.id}
          masterDisplayName={historyTarget.name}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {editing && (
        <Card padding="lg" className="border-primary-300">
          <h2 className="text-lg font-bold mb-3">{isNew ? '新規作成' : '編集'}</h2>
          <div className="grid grid-cols-1 gap-3">
            {isNew && (
              <div>
                <label className="block text-xs text-slate-600 mb-1" htmlFor="m7-id">
                  ドキュメント ID (id) — kebab-case、保存後変更不可{' '}
                  <span className="text-danger-600">*</span>
                </label>
                <input
                  id="m7-id"
                  type="text"
                  value={editing.id}
                  onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="例: ecw-default-card"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m7-name">
                表示名称 (displayName) <span className="text-danger-600">*</span>
              </label>
              <input
                id="m7-name"
                type="text"
                value={editing.displayName}
                onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m7-mode">
                モード (mode)
              </label>
              {/* MD-3 モードドリブン化: 選択肢は ModeProfile (M10) 登記簿から動的生成 (未登録の現在値も表示)。 */}
              <ModeSelectField
                id="m7-mode"
                value={editing.mode}
                onChange={(mode) => setEditing({ ...editing, mode })}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m7-layout">
                レイアウト (layout)
              </label>
              <select
                id="m7-layout"
                value={editing.layout}
                onChange={(e) => setEditing({ ...editing, layout: e.target.value as Layout })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                {LAYOUTS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>

            {/* fields[] 編集 */}
            <div className="border-t border-slate-200 pt-3 mt-2">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-bold text-slate-700">
                  項目 (fields[]、印刷フィールド、{editing.fields.length} 件)
                </label>
                <Button variant="secondary" size="sm" onClick={addField}>
                  ＋ field 追加
                </Button>
              </div>
              {editing.fields.length === 0 ? (
                <p className="text-xs text-slate-500">
                  field がありません。「＋ field 追加」で追加。
                </p>
              ) : (
                <ul className="grid grid-cols-1 gap-3">
                  {editing.fields.map((f, i) => (
                    <li
                      key={f.key.trim() || `__draft-${i}`}
                      className="border border-slate-200 rounded-md p-3 bg-slate-50"
                    >
                      <div className="grid grid-cols-1 gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-slate-500 shrink-0">#{i + 1}</span>
                          <input
                            type="text"
                            value={f.key}
                            onChange={(e) => updateField(i, { key: e.target.value })}
                            className={`${INPUT_CLASS} flex-1 min-w-24`}
                            placeholder="key"
                            aria-label={`項目 ${i + 1} キー (key)`}
                          />
                          <input
                            type="text"
                            value={f.label}
                            onChange={(e) => updateField(i, { label: e.target.value })}
                            className={`${INPUT_CLASS} flex-1 min-w-24`}
                            placeholder="label"
                            aria-label={`項目 ${i + 1} 表示名 (label)`}
                          />
                          <select
                            value={f.source}
                            onChange={(e) =>
                              updateField(i, { source: e.target.value as FieldSource })
                            }
                            className={`${INPUT_CLASS} w-32 shrink-0`}
                            aria-label={`項目 ${i + 1} ソース (source)`}
                          >
                            {FIELD_SOURCES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeField(i)}
                            aria-label={`項目 ${i + 1} 削除`}
                          >
                            ✕
                          </Button>
                        </div>
                        <input
                          type="text"
                          value={f.fieldPath}
                          onChange={(e) => updateField(i, { fieldPath: e.target.value })}
                          className={INPUT_CLASS}
                          placeholder="fieldPath (例: aiAnalysis.bousai.classifications[0].category_name)"
                          aria-label={`項目 ${i + 1} フィールドパス (fieldPath)`}
                        />
                        <details>
                          <summary className="text-xs text-slate-600 cursor-pointer">
                            style (任意、未設定は payload から省略)
                          </summary>
                          <div className="flex items-center gap-2 flex-wrap text-xs mt-2">
                            <label className="flex items-center gap-1 text-slate-700">
                              fontSize:
                              <input
                                type="number"
                                value={f.styleFontSize}
                                onChange={(e) => updateField(i, { styleFontSize: e.target.value })}
                                className="w-16 px-2 py-1 border border-slate-300 rounded"
                                placeholder="(空 = 未設定)"
                                aria-label={`項目 ${i + 1} スタイル フォントサイズ (style.fontSize)`}
                              />
                            </label>
                            <label className="flex items-center gap-1 text-slate-700">
                              <input
                                type="checkbox"
                                checked={f.styleBold}
                                onChange={(e) => updateField(i, { styleBold: e.target.checked })}
                                className="cursor-pointer"
                              />
                              bold
                            </label>
                            <label className="flex items-center gap-1 text-slate-700">
                              color:
                              <ColorSwatch
                                value={f.styleColor || '#0f172a'}
                                onChange={(c) => updateField(i, { styleColor: c ?? '' })}
                                showClear={false}
                                size={24}
                                ariaLabel={`項目 ${i + 1} スタイル 色 (style.color)`}
                              />
                              {f.styleColor ? (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => updateField(i, { styleColor: '' })}
                                  aria-label={`項目 ${i + 1} スタイル 色 クリア (style.color)`}
                                >
                                  クリア
                                </Button>
                              ) : (
                                <span className="text-[10px] text-slate-400">テーマ既定</span>
                              )}
                            </label>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap text-xs mt-2 pt-2 border-t border-slate-200">
                            <label className="flex items-center gap-1 text-slate-700">
                              <input
                                type="checkbox"
                                checked={f.styleShowLabel}
                                onChange={(e) =>
                                  updateField(i, { styleShowLabel: e.target.checked })
                                }
                                className="cursor-pointer"
                              />
                              項目名を表示 (showLabel)
                            </label>
                            <label className="flex items-center gap-1 text-slate-700">
                              <input
                                type="checkbox"
                                checked={f.styleResolveCodes}
                                onChange={(e) =>
                                  updateField(i, { styleResolveCodes: e.target.checked })
                                }
                                className="cursor-pointer"
                              />
                              コード→日本語名 (resolveCodes)
                            </label>
                            <label className="flex items-center gap-1 text-slate-700">
                              最大文字数:
                              <input
                                type="number"
                                min={0}
                                value={f.styleMaxChars}
                                onChange={(e) => updateField(i, { styleMaxChars: e.target.value })}
                                className="w-16 px-2 py-1 border border-slate-300 rounded"
                                placeholder="無制限"
                                aria-label={`項目 ${i + 1} 最大文字数 (style.maxChars)`}
                              />
                            </label>
                            <label className="flex items-center gap-1 text-slate-700">
                              改行:
                              <select
                                value={f.styleWrap}
                                onChange={(e) =>
                                  updateField(i, {
                                    styleWrap: e.target.value as FieldDraft['styleWrap'],
                                  })
                                }
                                className="px-2 py-1 border border-slate-300 rounded bg-white"
                                aria-label={`項目 ${i + 1} 改行 (style.wrap)`}
                              >
                                <option value="wrap">折り返す</option>
                                <option value="nowrap">折り返さない</option>
                              </select>
                            </label>
                            {f.styleWrap === 'wrap' && (
                              <label className="flex items-center gap-1 text-slate-700">
                                最大行数:
                                <input
                                  type="number"
                                  min={1}
                                  max={8}
                                  value={f.styleMaxLines}
                                  onChange={(e) =>
                                    updateField(i, { styleMaxLines: e.target.value })
                                  }
                                  className="w-14 px-2 py-1 border border-slate-300 rounded"
                                  placeholder="2"
                                  aria-label={`項目 ${i + 1} 最大行数 (style.maxLines)`}
                                />
                              </label>
                            )}
                            <label className="flex items-center gap-1 text-slate-700">
                              自動調整:
                              <select
                                value={f.styleFit}
                                onChange={(e) =>
                                  updateField(i, { styleFit: e.target.value as FitMode })
                                }
                                className="px-2 py-1 border border-slate-300 rounded bg-white"
                                aria-label={`項目 ${i + 1} 自動調整 (style.fit)`}
                              >
                                <option value="fixed">固定</option>
                                <option value="shrink">自動縮小</option>
                                <option value="grow">行数可変</option>
                              </select>
                            </label>
                          </div>
                        </details>
                        <div className="flex items-center gap-2 text-xs">
                          <label className="flex items-center gap-1 text-slate-700">
                            displayOrder:
                            <input
                              type="number"
                              value={f.displayOrder}
                              onChange={(e) =>
                                updateField(i, { displayOrder: Number(e.target.value) })
                              }
                              className="w-16 px-2 py-1 border border-slate-300 rounded"
                              aria-label={`項目 ${i + 1} 表示順 (displayOrder)`}
                            />
                          </label>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-slate-500 mt-2">
                保存時に displayOrder 昇順でソートされます。
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-2 border-t border-slate-200 pt-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setEditing(null);
                  setIsNew(false);
                }}
                disabled={saving}
              >
                キャンセル
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
